import { useMemoizedFn } from 'ahooks'
import Image from 'next/image'
import React, { useRef } from 'react'

import type { ImageProps } from 'next/image'

import { useNormalizePublicSrc } from '@/hooks/public'

export interface ILazyImageProps extends ImageProps {
  alt: string
  /** 是否启用懒加载模式，默认开启特殊情况下可关闭 */
  lazy?: boolean
  /** 图片加载失败时的兜底地址，可选 */
  fallbackSrc?: string
  /** 图片加载完成后是否添加 bg-checkered 类名, 透明图片显示透明背景 */
  loadAddBgCheckered?: boolean
}

const LazyImage: React.FC<ILazyImageProps> = (props) => {
  const {
    alt,
    lazy = true,
    style,
    src,
    fallbackSrc,
    onError,
    loadAddBgCheckered = false,
    ...rest
  } = props

  // 避免 fallbackSrc 异常导致 onError 死循环
  const fallbackRef = useRef(false)
  const imgRef = useRef<HTMLImageElement>(null)
  const normalizePublicSrc = useNormalizePublicSrc()

  const handleError: React.ReactEventHandler<HTMLImageElement> = useMemoizedFn(
    (event) => {
      if (fallbackSrc && !fallbackRef.current) {
        ;(event.target as HTMLImageElement).src =
          normalizePublicSrc(fallbackSrc)
        fallbackRef.current = true
      }
      onError?.(event)
    },
  )

  const priority = rest.priority
  let loading: 'eager' | 'lazy' = priority || !lazy ? 'eager' : 'lazy'
  // loading = 'lazy' 时，svg 图片不会显示，需要设置为 'eager'
  if (typeof src === 'string' && src.endsWith('.svg')) {
    loading = 'eager'
  }

  const fetchPriority = priority ? 'high' : 'low'

  const reactDoesNotRecognizeProps = {
    fetchpriority: fetchPriority,
  }

  const handleLoad = useMemoizedFn(
    (...args: Parameters<NonNullable<ImageProps['onLoad']>>) => {
      rest.onLoad?.(...args)

      if (loadAddBgCheckered) {
        const currentTarget = args[0].currentTarget
        currentTarget.classList.add('bg-checkered')
      }
    },
  )

  /** unoptimized 情况下有懒加载但是不调用 loader 函数 */
  return (
    <Image
      ref={imgRef}
      alt={alt}
      unoptimized
      src={
        typeof src === 'string'
          ? normalizePublicSrc(src)
          : src /** next/image 支持的 src 特殊类型处理 */
      }
      onError={handleError}
      style={style}
      loading={loading}
      {...reactDoesNotRecognizeProps}
      {...rest}
      onLoad={handleLoad}
    />
  )
}

export default LazyImage
